import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCheck, ChevronDown, ChevronRight, EyeOff, FileSearch, Loader2, Scale, Sparkles, X } from 'lucide-react';
import { describeError } from '../../services/errorMessage';
import { einschaetzeBaubeschreibung, KI_ABGEBROCHEN } from '../../services/planungKi/planungKiService';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { KiUnterlagenDialog } from './KiUnterlagenDialog';
import {
    eindeutigeOptionen,
    fasseZaehlerZusammen,
    kiBelegText,
    kiBeschreibungErgebnis,
    kiErgebnisFehler,
    kiMerkmalAnfragen,
    kiQuellenHinweis,
    kiSeitenWarnungen,
    kiWertText,
    kiZitatEtikett,
    werteKiVorschlaegeAus,
    type KiBeleg,
    type KiFeldGruppe,
    type KiLeseErgebnis,
    type KiWertOption,
} from './planungKiAnzeige';
import type { ProjektMerkmale } from './types';

interface Props {
    projectId: string;
    /** Merkmale des ENTWURFS: daraus die Anfragen, und ein gesetztes Feld
     *  laesst seinen Vorschlag verschwinden (wie Stufe 2.1). */
    merkmale: ProjektMerkmale;
    /** Baubeschreibung und ergaenzende Angaben des Entwurfs (`kiBeschreibungText`)
     *  — geht NUR auf Klick „Baubeschreibung auswerten" an die KI. */
    beschreibung: string;
    /** Ergebnis des letzten Laufs. Haelt das Modul, damit ein Reiterwechsel
     *  es nicht vergisst; gespeichert wird es nie. */
    ergebnis: KiLeseErgebnis | null;
    /** In dieser Sitzung ausgeblendete Werte (`ki:feld:wert`). */
    ausgeblendet: ReadonlySet<string>;
    /** Nur-Ansicht oder abgeschlossen: Lesen und Uebernehmen sind aus. */
    isReadOnly: boolean;
    onErgebnis: (ergebnis: KiLeseErgebnis) => void;
    onVerwerfen: () => void;
    onUebernehmen: (option: KiWertOption) => void;
    onAusblenden: (id: string) => void;
    /**
     * Meldet Beginn (`true`) und Ende (`false`) einer Auswertung der
     * Baubeschreibung. Die Leiste bricht ihren Lauf ab, sobald sie
     * ausgehaengt wird — der Container sperrt damit Wechsel, die sie aushaengen
     * wuerden. Gerufen aus dem Klick-Handler selbst (Start) und aus dessen
     * `finally` (Ende, auch nach Abbruch und Fehler), nie aus einem Effekt.
     * Das Lesen aus Unterlagen laeuft im Dialog: der deckt die Seite ab, ein
     * Wechsel ist dort nicht erreichbar.
     */
    onLaeuft?: (laeuft: boolean) => void;
}

const KNOPF = 'inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border '
    + 'disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * Das Zitat — zugeklappt zwei Zeilen, kursiv; Klick zeigt alles. Das
 * Etikett davor sagt, was es ist: Text aus dem Dokument (geprueft) oder die
 * Lesung der KI aus einem Scan (ungeprueft) — nie ohne Etikett, damit kein
 * Dokumenttext wie eine Formulierung der KI aussieht und umgekehrt.
 */
const Zitat: React.FC<{ beleg: KiBeleg }> = ({ beleg }) => {
    const [auf, setAuf] = useState(false);
    // Einschaetzung ohne Zitat: die Begruendung der KI, nie in Anfuehrungszeichen
    // — sie steht nicht in der Baubeschreibung.
    if (beleg.einschaetzung) {
        return (
            <div className="mt-1">
                <div className="text-[11px] text-slate-500 leading-snug">
                    <span className="font-bold">Quelle:</span> {kiBelegText(beleg)}
                </div>
                <div className="text-xs text-slate-700 bg-white/70 border-l-2 border-amber-300 pl-2 py-0.5 mt-0.5 break-words">
                    <span className="font-bold text-amber-700">{kiZitatEtikett(beleg)}</span>{' '}
                    {beleg.einschaetzung}
                </div>
            </div>
        );
    }
    return (
        <div className="mt-1">
            <div className="text-[11px] text-slate-500 leading-snug">
                <span className="font-bold">Quelle:</span> {kiBelegText(beleg)}
            </div>
            <button
                type="button"
                onClick={() => setAuf(a => !a)}
                aria-expanded={auf}
                title={auf ? 'Zitat zuklappen' : 'Ganzes Zitat zeigen'}
                className={`${auf ? 'block' : 'line-clamp-2'} w-full text-left text-xs text-slate-700 bg-white/70 border-l-2 border-slate-300 pl-2 py-0.5 mt-0.5 whitespace-pre-wrap break-words`}
            >
                <span className={`not-italic font-bold ${beleg.geprueft ? 'text-slate-500' : 'text-amber-700'}`}>{kiZitatEtikett(beleg)}</span>{' '}
                <span className="italic">„{beleg.zitat}“</span>
            </button>
            {!beleg.geprueft && (
                <div className="text-[11px] font-bold text-amber-700 mt-0.5">
                    Zitat nicht prüfbar (Scan) — bitte im Dokument nachsehen.
                </div>
            )}
        </div>
    );
};

/** Ein vorgeschlagener Wert mit seinen Belegen. Nein ist gelb: es blendet Fragen aus. */
const WertKarte: React.FC<{
    option: KiWertOption;
    isReadOnly: boolean;
    onUebernehmen: () => void;
    onAusblenden: () => void;
}> = ({ option, isReadOnly, onUebernehmen, onAusblenden }) => (
    <div className={`rounded-lg border px-3 py-2 ${option.nein ? 'bg-amber-50 border-amber-300' : 'bg-white border-blue-200'}`}>
        <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1 text-xs leading-snug">
                <span className="text-slate-400">→ </span>
                <span className={`font-bold ${option.nein ? 'text-amber-800' : 'text-blue-700'}`}>{kiWertText(option)}</span>
                {option.belege.length > 1 && (
                    <span className="text-slate-400"> · {option.belege.length} Belege</span>
                )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <button
                    type="button"
                    onClick={onUebernehmen}
                    disabled={isReadOnly}
                    title={isReadOnly ? readOnlyTitle('Übernehmen') : 'In die Grundlagen übernehmen — wird automatisch gespeichert'}
                    className={`${KNOPF} ${option.nein
                        ? 'text-amber-800 bg-white border-amber-300 hover:bg-amber-100'
                        : 'text-blue-700 bg-white border-blue-300 hover:bg-blue-100'}`}
                >
                    <Check className="w-3.5 h-3.5" /> Übernehmen
                </button>
                <button
                    type="button"
                    onClick={onAusblenden}
                    disabled={isReadOnly}
                    title={isReadOnly ? readOnlyTitle('Ausblenden') : 'Für diese Sitzung ausblenden — nichts wird gespeichert'}
                    className={`${KNOPF} text-slate-500 bg-white border-slate-200 hover:bg-slate-50`}
                >
                    <EyeOff className="w-3.5 h-3.5" /> Ausblenden
                </button>
            </div>
        </div>
        {option.nein && (
            <div className="text-[11px] font-bold text-amber-800 mt-1 leading-snug">
                {option.geschaetzt
                    ? 'Eingeschätzt, nicht belegt: ein Nein blendet Fragen aus — nur übernehmen, wenn es für dieses Vorhaben sicher zutrifft.'
                    : 'Ein Nein blendet Fragen aus — nur übernehmen, wenn das Zitat es eindeutig sagt.'}
            </div>
        )}
        {option.belege.map(b => <Zitat key={b.vorschlagId} beleg={b} />)}
    </div>
);

const GruppenZeile: React.FC<{
    gruppe: KiFeldGruppe;
    isReadOnly: boolean;
    onUebernehmen: (o: KiWertOption) => void;
    onAusblenden: (id: string) => void;
}> = ({ gruppe, isReadOnly, onUebernehmen, onAusblenden }) => (
    <li className="px-4 py-2.5">
        <div className="text-xs text-slate-800 leading-snug mb-1.5">
            <span className="font-bold">{gruppe.label}</span>
            {gruppe.widerspruch && (
                <span className="inline-flex items-center gap-1 ml-2 text-[11px] font-bold text-amber-800">
                    <Scale className="w-3.5 h-3.5" />
                    Die Dokumente widersprechen sich — bitte selbst entscheiden, keiner ist vorausgewählt.
                    {gruppe.ausgeblendet > 0 && <span className="font-normal text-slate-500">({gruppe.ausgeblendet} ausgeblendet)</span>}
                </span>
            )}
        </div>
        <div className={`grid gap-2 ${gruppe.optionen.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
            {gruppe.optionen.map(o => (
                <WertKarte
                    key={o.id}
                    option={o}
                    isReadOnly={isReadOnly}
                    onUebernehmen={() => onUebernehmen(o)}
                    onAusblenden={() => onAusblenden(o.id)}
                />
            ))}
        </div>
    </li>
);

/**
 * „Aus Unterlagen lesen (KI)" (Plan Stufe 7.1) — direkt unter der
 * Vorschlagsleiste aus den Projektdaten, im selben Stil.
 *
 * Die Leiste zeigt nur an und ruft auf. Was gefragt wird, wie Vorschlaege
 * gruppiert werden und was eine Uebernahme am Entwurf aendert, steht geprueft
 * in `planungKiAnzeige.ts`. Dieselben Zusagen wie in 2.1: nichts wird von
 * selbst gesetzt, ein Vorschlag verschwindet, sobald sein Feld gesetzt ist,
 * „Ausblenden" gilt nur fuer diese Sitzung. Dazu: jede Zeile nennt Dokument,
 * Seite und woertliches Zitat; ein Fehler sieht nie aus wie „nichts gefunden".
 */
export const PlanungKiLeiste: React.FC<Props> = ({
    projectId, merkmale, beschreibung, ergebnis, ausgeblendet, isReadOnly,
    onErgebnis, onVerwerfen, onUebernehmen, onAusblenden, onLaeuft,
}) => {
    const [offen, setOffen] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [schaetzt, setSchaetzt] = useState(false);
    const abbruch = useRef<AbortController | null>(null);

    // Wird die Leiste ausgehaengt (Reiter, Projekt), endet ein laufender Aufruf.
    useEffect(() => () => abbruch.current?.abort(), []);

    const offeneAnfragen = useMemo(() => kiMerkmalAnfragen(merkmale).length, [merkmale]);
    const beschreibungLeer = !String(beschreibung || '').trim();

    /** „Baubeschreibung auswerten (KI)": EIN Aufruf mit dem Text der Maske.
     *  Ein Abbruch laesst das vorige Ergebnis stehen; jedes andere Ende wird
     *  angezeigt — ein Fehler sieht nie aus wie „nichts gefunden". */
    const werteBeschreibungAus = async () => {
        if (isReadOnly || schaetzt) return;
        const anfragen = kiMerkmalAnfragen(merkmale);
        const ctrl = new AbortController();
        abbruch.current = ctrl;
        setSchaetzt(true);
        onLaeuft?.(true);
        try {
            const lauf = await einschaetzeBaubeschreibung(beschreibung, anfragen, { signal: ctrl.signal });
            if (ctrl.signal.aborted || lauf.fehler === KI_ABGEBROCHEN) return;
            onErgebnis(kiBeschreibungErgebnis(projectId, anfragen, lauf, new Date().toISOString()));
            setOffen(true);
        } catch (e) {
            if (!ctrl.signal.aborted) onErgebnis(kiBeschreibungErgebnis(projectId, anfragen, null, new Date().toISOString(), describeError(e)));
        } finally {
            if (abbruch.current === ctrl) abbruch.current = null;
            setSchaetzt(false);
            // Auch nach dem Abbruch beim Aushaengen: der Container darf nicht
            // gesperrt bleiben (der abgebrochene Aufruf endet sofort, siehe
            // `mitAbbruch` im Service).
            onLaeuft?.(false);
        }
    };

    const schaetzenGesperrt = isReadOnly || schaetzt || offeneAnfragen === 0 || beschreibungLeer;
    const schaetzenTitel = isReadOnly
        ? readOnlyTitle('Baubeschreibung auswerten')
        : offeneAnfragen === 0
            ? 'Alle Merkmale sind gesetzt — es gibt nichts einzuschätzen.'
            : beschreibungLeer
                ? 'Erst die Baubeschreibung eintragen.'
                : 'Die KI schlägt aus der Baubeschreibung Ja oder Nein für die offenen Merkmale vor';
    // Ein Ergebnis gehoert zu EINEM Projekt — wechselt das Projekt unter dem
    // Modul, wird das alte nicht angezeigt.
    const aktuell = ergebnis && ergebnis.projectId === projectId ? ergebnis : null;
    const auswertung = useMemo(
        () => (aktuell && aktuell.lauf
            ? werteKiVorschlaegeAus(aktuell.lauf.vorschlaege, aktuell.anfragen, merkmale, ausgeblendet)
            : null),
        [aktuell, merkmale, ausgeblendet],
    );
    const fehler = kiErgebnisFehler(aktuell);
    const verworfen = fasseZaehlerZusammen(aktuell?.lauf?.verworfen, auswertung?.verworfen);
    const gruppen = useMemo(() => (auswertung ? auswertung.gruppen : []), [auswertung]);
    const eindeutig = useMemo(() => eindeutigeOptionen(gruppen), [gruppen]);
    const eindeutigNein = eindeutig.filter(o => o.nein).length;
    const hatteVorschlaege = !!aktuell?.lauf && aktuell.lauf.vorschlaege.length > 0;

    const lesenGesperrt = isReadOnly || offeneAnfragen === 0;
    const lesenTitel = isReadOnly
        ? readOnlyTitle('Aus Unterlagen lesen')
        : offeneAnfragen === 0
            ? 'Alle Merkmale sind gesetzt — es gibt nichts zu fragen.'
            : 'PDFs aus der Ablage oder von diesem Rechner auswählen und von der KI lesen lassen';

    return (
        <section className="bg-blue-50 border border-blue-200 rounded-2xl mb-6">
            <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap">
                <button
                    type="button"
                    onClick={() => setOffen(o => !o)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    aria-expanded={offen}
                >
                    {offen ? <ChevronDown className="w-4 h-4 text-blue-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-blue-500 shrink-0" />}
                    <Sparkles className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="text-sm font-bold text-blue-900 truncate">
                        Vorschläge der KI{aktuell ? ` (${gruppen.length})` : ''}
                    </span>
                </button>
                {aktuell && !schaetzt && (
                    <button
                        type="button"
                        onClick={onVerwerfen}
                        title="Ergebnis dieses Laufs verwerfen — im Entwurf bleibt alles, wie es ist"
                        className={`${KNOPF} text-slate-500 bg-white border-slate-200 hover:bg-slate-50 shrink-0`}
                    >
                        <X className="w-3.5 h-3.5" /> Ergebnis verwerfen
                    </button>
                )}
                {schaetzt ? (
                    <span className="inline-flex items-center gap-2 shrink-0">
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-800">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Die KI liest die Baubeschreibung …
                        </span>
                        <button
                            type="button"
                            onClick={() => abbruch.current?.abort()}
                            className={`${KNOPF} text-slate-600 bg-white border-slate-200 hover:bg-slate-50`}
                        >
                            <X className="w-3.5 h-3.5" /> Abbrechen
                        </button>
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={() => { void werteBeschreibungAus(); }}
                        disabled={schaetzenGesperrt}
                        title={schaetzenTitel}
                        className={`${KNOPF} text-white bg-blue-600 border-blue-600 hover:bg-blue-700 shrink-0`}
                    >
                        <Sparkles className="w-3.5 h-3.5" /> Baubeschreibung auswerten (KI)
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setDialog(true)}
                    disabled={lesenGesperrt || schaetzt}
                    title={lesenTitel}
                    className={`${KNOPF} text-blue-700 bg-white border-blue-300 hover:bg-blue-100 shrink-0`}
                >
                    <FileSearch className="w-3.5 h-3.5" /> Aus Unterlagen lesen (KI)
                </button>
            </div>

            {offen && (
                <div className="border-t border-blue-200">
                    {!aktuell && (
                        <p className="px-4 py-2.5 text-[11px] text-blue-800 leading-snug">
                            <strong>Baubeschreibung auswerten:</strong> Die KI schätzt aus Ihrer Baubeschreibung, welche
                            Merkmale vorkommen (Ja nur mit Zitat) und welche bei diesem Vorhaben ausgeschlossen sind (Nein mit
                            Zitat oder begründeter Einschätzung). Jedes Nein blendet Fragen aus — so wird der Katalog kürzer.{' '}
                            <strong>Aus Unterlagen lesen:</strong> dasselbe aus PDFs (Genehmigung, Gutachten, LV), jeweils mit
                            Seite und wörtlichem Zitat. Nichts wird von selbst gesetzt; was Sie übernehmen, wird
                            automatisch gespeichert.
                        </p>
                    )}
                    <p className="px-4 pb-2 text-[11px] text-slate-500 leading-snug">
                        Datenschutz: Bei „Baubeschreibung auswerten“ gehen Baubeschreibung und ergänzende Angaben an die
                        KI (Google Gemini) — bitte keine Namen, Anschriften oder Telefonnummern hineinschreiben.
                    </p>

                    {aktuell && (
                        <div className="px-4 pt-2.5 space-y-1.5">
                            <p className="text-[11px] text-blue-800 leading-snug">
                                Gelesen {new Date(aktuell.gelesenAm).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} —
                                nichts davon ist gesetzt. Übernommen wird je Zeile in die Grundlagen und automatisch
                                gespeichert. „Ausblenden“ gilt nur für diese Sitzung.
                            </p>
                            <ul className="text-[11px] text-slate-600 leading-snug space-y-0.5">
                                {aktuell.dokumente.map((d, i) => (
                                    <li key={`${d.name}-${i}`}>
                                        <span className="font-bold">{d.name}</span>
                                        {d.herkunft === 'datei' && <span className="text-slate-400"> (lokale Datei)</span>}
                                        {': '}
                                        {d.fehler
                                            ? <span className="text-rose-700 font-bold">nicht gelesen — {d.fehler}</span>
                                            : d.herkunft === 'grundlagen'
                                                ? <span>Baubeschreibung und ergänzende Angaben aus dieser Maske gingen an die KI.</span>
                                                : <span>{kiQuellenHinweis(d)}</span>}
                                        {!d.fehler && kiSeitenWarnungen(d, aktuell.lauf?.verworfen).map(w => (
                                            <span key={w} className="block mt-0.5 font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                                                {w}
                                            </span>
                                        ))}
                                    </li>
                                ))}
                            </ul>

                            {fehler && (
                                <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Die KI-Auswertung ist fehlgeschlagen</strong> — das heißt nicht, dass nichts
                                        gefunden wurde. {fehler}
                                    </span>
                                </div>
                            )}
                            {verworfen.length > 0 && (
                                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                    <span className="font-bold">Verworfen:</span>{' '}
                                    {verworfen.map(z => `${z.anzahl} × ${z.grund}`).join(' · ')}
                                </div>
                            )}
                            {!fehler && eindeutig.length > 1 && (
                                <div className="flex items-center gap-2 flex-wrap pt-0.5">
                                    <button
                                        type="button"
                                        onClick={() => eindeutig.forEach(onUebernehmen)}
                                        disabled={isReadOnly}
                                        title={isReadOnly ? readOnlyTitle('Übernehmen') : 'Alle Felder ohne Widerspruch in die Grundlagen übernehmen — wird automatisch gespeichert'}
                                        className={`${KNOPF} text-white bg-blue-600 border-blue-600 hover:bg-blue-700`}
                                    >
                                        <CheckCheck className="w-3.5 h-3.5" /> Alle {eindeutig.length} übernehmen
                                        {eindeutigNein > 0 ? ` (davon ${eindeutigNein} × Nein)` : ''}
                                    </button>
                                    {eindeutigNein > 0 && (
                                        <span className="text-[11px] text-amber-800">
                                            Bitte die Nein-Zeilen vorher kurz ansehen — sie blenden Fragen aus.
                                        </span>
                                    )}
                                </div>
                            )}
                            {!fehler && gruppen.length === 0 && (
                                <p className="text-xs text-slate-600 pb-1">
                                    {hatteVorschlaege
                                        ? 'Keine offenen Vorschläge mehr — übernommen, ausgeblendet oder inzwischen selbst gesetzt.'
                                        : `Zu den ${aktuell.anfragen.length} gefragten Merkmalen hat die KI keinen belegten Hinweis gefunden. Sie bleiben offen.`}
                                </p>
                            )}
                        </div>
                    )}

                    {gruppen.length > 0 && (
                        <ul className="divide-y divide-blue-100 mt-1">
                            {gruppen.map(g => (
                                <GruppenZeile
                                    key={g.feld}
                                    gruppe={g}
                                    isReadOnly={isReadOnly}
                                    onUebernehmen={onUebernehmen}
                                    onAusblenden={onAusblenden}
                                />
                            ))}
                        </ul>
                    )}
                    {aktuell && gruppen.length === 0 && <div className="pb-1.5" />}
                </div>
            )}

            {dialog && (
                <KiUnterlagenDialog
                    projectId={projectId}
                    merkmale={merkmale}
                    isReadOnly={isReadOnly}
                    onFertig={e => { setDialog(false); setOffen(true); onErgebnis(e); }}
                    onSchliessen={() => setDialog(false)}
                />
            )}
        </section>
    );
};
